/* eslint-disable @typescript-eslint/no-unused-vars */
// /* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// /* eslint-disable @typescript-eslint/no-explicit-any */
// /* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useEffect,
  useState
} from 'react';
import { Global, css } from '@emotion/react';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { docReady } from 'src/utils/Document';
import Head from 'src/components/Head';
import { logPageError, logPageRequest, logPageRequestDebug } from "../lib/helper/logger";
import { removeUndefined } from 'lib/utils/serialization';
import { getClientSafeApiKeys, ClientSafeApiKeys } from 'lib/repository/shared-repository/shared-repository';
import { getSortedModalPriorityList, type ModalType } from 'lib/utils/modal-priority';
import { resolveModalFeeds } from 'lib/utils/resolve-modal-feeds';
import type { IModalOrchestratorProps } from 'src/components/video-lab/modal-orchestrator/modal-orchestrator';
import { findDuplicateIds } from 'src/utils/UtilityFunctions';

import WnCMainCanvas from 'src/components/WnCMainCanvas';
import ReportingService from 'src/services/ReportingService';
import { pageTags } from 'src/constants/PageTags';

const Error = dynamic(() => import("./_error"));
const PersonalizationSideMenu = dynamic(() => import("src/components/PersonlizationSideMenu/PersonalizationSideMenu").then(mod => mod.PersonalizationSideMenu), { ssr: false });

/** ---------------------------------------------------------------------------------
 * Theme --------------------------------------------------------------------------- */
import DtvTheme, { colorPalette as Colors } from '@directv-digital/dds.ui.dtv-theme';
import { ThemeProvider as WnCThemeProvider } from '@mui/material/styles';
import WnCCssBaseline from '@mui/material/CssBaseline';
import { Gradiant_Colors } from 'src/components/Background/BackgrondProps';
import { BrandColor } from 'src/constants/CssProperties';
import { getCurrentBreakpoint } from 'src/utils/Breakpoint/Breakpoint';

/** ---------------------------------------------------------------------------------
 * Utils -------------------------------------------------------------------------- */
import ClientPageBootstrap from 'src/components/ClientPageBootStrap';
import DocumentEventHandler from '@directv-digital/dds.ui.document-event-handler';
import { getCookie, setCookie } from '@directv-digital/dds.services.cookie-service';
import {
  fireModifiedComponentTypePageLoadEvent
} from "@directv-digital/dds.services.analytics-service";
import { fireDynamicComponentNotDisplayedEvent } from "lib/helper/analytics/fire-dynamic-component-not-displayed";

const { publicRuntimeConfig } = getConfig();

/** ---------------------------------------------------------------------------------
 * Components --------------------------------------------------------------------- */
import DtvGlobalHeader from  "@directv-digital/dds.ui.global-header-v3";
import GlobalModal from "@/video-lab/global-modal";
import { dynamicPageAdapter } from 'lib/adapters/storyblok';

const ModalOrchestrator = dynamic(() => import('@/video-lab/modal-orchestrator'), { ssr: false })
const DtvFlash = dynamic(() => import("@/video-lab/dtv-flash"));
const DtvFooter = dynamic(() => import("@directv-digital/dds.ui.dtv-footer"));
const DtvLinkFarm = dynamic(() => import("@directv-digital/dds.ui.linkfarm"));
const ErrorDisplay = dynamic(() => import("@directv-digital/dds.ui.error-display"), { ssr: false });
const DtvSnackBar = dynamic(() => import('@/video-lab/dtv-snackbar'))
import { useStoryblokLivePage } from "../hooks/useStoryblokLivePage";

interface IndexProps {
  data: any;
  feedData: any;
  err?: { statusCode: number };
  feed: any;
  // isOrc: boolean;
  pagePath: string;
  page: string;
  pages: any;
  root: string;
  zipCode?: number | string;
  depVersions?: any;
  userAgent?: string; // to-do: need this?
  globalNavHeader?: any;
  globalNavLinkFarm?: any;
  idleTimerModal?: any;
  is404Page?: boolean;
  reqQueryParams?: any;
  classes?: any;
  isUserAuthenticated?: boolean;
  disableGNav?: boolean;
  // isV2GNav?: boolean;
  hideCartIcon?: boolean;
  disableSkinnyGNav?: boolean;
  disableLinkFarm?: boolean;
  disableGlobalFooter?: boolean;
  // isV2LinkFarm?: boolean;
  // isV2Footer?: boolean;
  disableAuxNav?: boolean;
  disableBusinessLink?: boolean;
  recognizedUserData?: any;
  // auxNav?: any;
  helpCenterInfoLabel?: any;
  customerCareNum?: any;
  customerCareNumLabel?: any;
  disableTFN?: boolean;
  pageThemeObj?: any;
  globalOverrides?: any;
  // linkfarmdata?: any;
  isDCP: boolean;
  sbConfigs?: any;
  apiPaths?: any;
  breadcrumbHomeURL: string;
  appVariant: string;
  cms_type: string;
  globalNavFooter: any;
  sb?: any;
  flashProps?: any;
  exitModalCMS?: any;
  globalFeaturesConfig?: any;
  globalheader?: any;
  globalRsnFee?: string;
  personalizedData?: any;
  dmaCookie?: number | string;
  subscriberType?: string;
  utmCodeCookie?: string;
  visitedPCP?: string;
  userVisitCount?: string;
  rsnFee?: number | string;
  cookieList?: any;
  pageBackgrounds?: any;
  /** WAT-10279: Sorted list of modal types in descending priority order */
  modalPriorityList?: ModalType[];
  /** WAT-10279: MarketingAI (Purchase Likelihood) modal CMS segment data */
  marketingAiModalData?: any;
  pageLevelGnProps?: any;
  previousURL?: any;
  /** Pre-resolved revisitor feed (page-level ?? global assembled, resolved in GSSP) */
  revisitorModalFeed?: any;
  packageConfig?: any;
  genrePackOfferIds?: any;
  onDemandPackageConfig?: any;
  apiKeys?: ClientSafeApiKeys;
  personalizedComponents?: any;
  rsnTierLegalTextObj?: any;
  styleOverwriteData?: { css: string };
  dataSource?: any;
  pageAdapterProps?: any;
}

interface FunctionalPage<P = { IndexProps }> extends React.FC<P> {
  getInitialProps?: (ctx: any) => any;
}

const DTVUFComIndex: FunctionalPage<IndexProps> = props => {
  const dtvFlashData = props.flashProps;
  const { pageThemeObj } = props;
  const disableSkipNavigation = !!dtvFlashData;
  const appEnv = publicRuntimeConfig?.local;

  const {
    components: liveComponents
  } = useStoryblokLivePage({
    sbPreviewAllowed: props.sbConfigs?.sbPreviewAllowed,
    pageAdapterProps: props.pageAdapterProps,
    initialComponents: props.data,
    sbRelationResolveKey: props.sbConfigs?.sbReferenceComponent
  });
  const sbData = props.sbConfigs?.sbPreviewAllowed ? liveComponents : props.data;

  // Do not render AuxNav if recognized
  const [disableAuxNav, setDisableAuxNav] = useState(props.recognizedUserData ? true : props.disableAuxNav);
  const [clientSideIsUserAuth, setClientSideIsUserAuth] = useState(props.isUserAuthenticated);
  const [clientSideRecognizedUserData, setClientSideRecognizedUserData] = useState(props.recognizedUserData);
  const isMduPage = props?.globalFeaturesConfig?.disableCartIconDrawerFor?.some(
    mduPage => mduPage?.url === props?.page
  );
  const [headerData, setHeaderData] = useState(props.globalNavHeader)

  const getPageBackground = () => {
    const pageContainer = props.pageBackgrounds?.pageContainer;
    if (pageContainer?.color) {
      const color = pageContainer.color.toLowerCase();
      return BrandColor[color] || pageContainer.color;
    }
    if (pageContainer?.gradient && Gradiant_Colors[pageContainer.gradient]) {
      return Gradiant_Colors[pageContainer.gradient];
    }
    return props.pagePath?.includes("channel-lineup") ? "white" : "inherit";
  };
  const pageBackground = getPageBackground();

  const getBodyBackground = () => {
    const bg = props.pageBackgrounds?.pageBody;
    if (bg?.color) {
      return BrandColor[bg.color.toLowerCase()] || bg.color;
    }
    if (bg?.gradient) {
      return Gradiant_Colors[bg.gradient] || Colors.white.main;
    }
    return Colors.white.main;
  };
  const bodyBackground = getBodyBackground();

  // set UF navigation history in session storage for shop team WAT-5710
  useEffect(() => {
    try {
      const currentPage = window.location.href;
      const history = JSON.parse(sessionStorage.getItem("navigationHistory") || "[]");
      if (history?.[history?.length - 1] !== currentPage && !props?.page?.includes?.("/modal")) {
        history.push(currentPage);
      }
      sessionStorage.setItem("navigationHistory", JSON.stringify(history || []));
    } catch (error) {
      console.error("Error updating navigation history:", error);
    }
  }, [props?.page]);

  // Check if page has duplicate/ incorrectly formatted IDs
  useEffect(() => {
    docReady(() => {
      findDuplicateIds();
    });
  }, [])

  // Consolidated Analytics Effect
  useEffect(() => {
    if (process.browser && window && window.DataMappingInterface) {
      window.DataMappingInterface.disableAutoPageLoad();
    }

    const triggerAnalytics = () => {
      // 1. Send Impressions
      const service: any = new ReportingService();
      service.sendImpressions(
        props || [],
        getCurrentBreakpoint(),
        props.page,
        props.pagePath,
        props.root,
        props.reqQueryParams
      );

      // 2. Fire Modified Component Analytics
      const customComponents = [];
      props?.data?.forEach(el => {
        if (el?.personalizationKey || el?.props?.analytics?.isPersonalizedComponent) {
          customComponents.push(el?.props?.analytics?.contentFriendlyName);
        }
      });

      if (customComponents.length > 0) {
        customComponents.forEach(component => {
          if(component) {
            fireModifiedComponentTypePageLoadEvent(component);
          }
        });
      }

      // WAT-10440: emit dim_name 'not displayed' for empty-default personalization
      // slots so the ineligible cohort has a GA row alongside the displayed cohort.
      // Additive only — existing 'displayed' fire above is unchanged.
      props?.data?.forEach(el => {
        if (!el?.personalizationKey) {
          return;
        }
        const isTypelessPlaceholder = !el?.type && !el?.props?.type;
        if (!isTypelessPlaceholder) {
          return;
        }
        fireDynamicComponentNotDisplayedEvent(
          el?.props?.analytics?.contentFriendlyName || el.personalizationKey
        );
      });
    };

    // Execute using requestIdleCallback or setTimeout
    let idleCallbackId: any;
    let timeoutId: any;

    if ('requestIdleCallback' in window) {
      idleCallbackId = requestIdleCallback(triggerAnalytics, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(triggerAnalytics, 0);
    }

    // 3. Third Party Tags (Deferred until user interaction OR 3 seconds)
    // This improves INP by keeping main thread free during initial interactions
    let thirdPartyLoaded = false;
    const loadThirdPartyTags = () => {
      if (thirdPartyLoaded) {
        return;
      }
      thirdPartyLoaded = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'marTechTrigger' });
    };

    // Load on first user interaction (click, scroll, keydown, touchstart)
    const interactionEvents = ['click', 'scroll', 'keydown', 'touchstart'];
    interactionEvents.forEach(event => {
      window.addEventListener(event, loadThirdPartyTags, {
        once: true,
        passive: true
      });
    });

    // Fallback: Load after 3 seconds if no interaction
    const thirdPartyTimeout = setTimeout(loadThirdPartyTags, 3000);

    return () => {
      if (idleCallbackId && 'cancelIdleCallback' in window) {
        cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      clearTimeout(thirdPartyTimeout);
      // Clean up interaction listeners
      interactionEvents.forEach(event => {
        window.removeEventListener(event, loadThirdPartyTags);
      });
    };
  }, [props.data]);

  const loadCookies = () => {
    if (props?.cookieList?.length > 0) {
      const oemCookie = props?.cookieList?.find?.(cookie => cookie?.type === "oem")
      if (oemCookie) {
        const name = oemCookie?.name;
        const value = oemCookie?.value;
        const domain = oemCookie?.options?.domain;
        if (![name, value, domain].includes(undefined) || ![name, value, domain].includes(null)) {
          setCookie(name, value, '/', 30, false, domain);
        }
      }

      const rpid = props?.cookieList?.find?.(cookie => cookie?.type === "rpid");
      if (rpid?.name && rpid?.value) {
        setCookie(rpid.name, rpid.value, '/', 30, false);
      }

      const utCode = props?.cookieList?.find?.(cookie => cookie?.name === "utCode")
      if (utCode) {
        setCookie(utCode.name, utCode.value, '/', 0, true);
      }
    }
    if (props.reqQueryParams?.agentId) {
      setCookie("agentId", props.reqQueryParams?.agentId, "/", 1, true, window.location.hostname);
    } else {
      document.cookie = "agentId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
  };

  useEffect(() => {
    const cAuthNStateCookie = getCookie("cAuthNState");
    const recogmeCookie = getCookie("recogme");
    const accTypeInCtxCookie = getCookie("accTypeInCtx");
    const authenicated = (cAuthNStateCookie && cAuthNStateCookie !== 'en') && typeof accTypeInCtxCookie !== 'undefined';
    const recognized = recogmeCookie && recogmeCookie !== '' && accTypeInCtxCookie && accTypeInCtxCookie !== '';

    if (authenicated) {
      const updatedHeaderData = {
        ...headerData,
        unauth: {
          ...headerData.unauth,
          right: headerData.auth?.right || [],
          mobileHead: headerData.auth?.mobileHead || []
        }
      };
      setDisableAuxNav(true);
      setHeaderData(updatedHeaderData);
    } else if (recognized) {
      const updatedHeaderData = {
        ...headerData,
        unauth: {
          ...headerData.unauth,
          right: headerData.recog?.right || [],
          mobileHead: headerData.recog?.mobileHead || []
        }
      };
      setHeaderData(updatedHeaderData);
    }

    if (typeof cAuthNStateCookie !== 'undefined' && clientSideIsUserAuth !== authenicated) {
      setClientSideIsUserAuth(authenicated);
    }

    if ((authenicated || recognized) && !props.recognizedUserData) {
      if (["ATTTV", "ATTTV_NONVIDEO", "ATTTV_ENABLER"].includes(accTypeInCtxCookie)) {
        setClientSideRecognizedUserData({ accountType: "stream" })
      } else if (["STMS", "NEWCO", "ENABLER"].includes(accTypeInCtxCookie)) {
        setClientSideRecognizedUserData({ accountType: "satellite" })
      }
    }

    loadCookies();

    // TO-DO: Remove this once we have the real event listener
    // z1_analytic event listener for special offers
    const handleZ1Analytic = event => {
      const { detail } = event;
      const { offerValue, offerCode } = JSON.parse(detail.data || '{}');
    };

    window.addEventListener("z1_analytic", handleZ1Analytic);

    // Setting user visit count cookie
    if (getCookie("userVisitCount")) {
      setCookie("userVisitCount", (parseInt(getCookie("userVisitCount")) + 1).toString(), "/", 180, false, ".directv.com");
    } else {
      setCookie("userVisitCount", "2", "/", 180, false, ".directv.com");
    }

    // Defer non-critical DOM cleanup to idle time
    const idleId = 'requestIdleCallback' in window ?
      requestIdleCallback(() => {
        document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(heading => {
          heading.removeAttribute('aria-label');
        });
      }) :
      setTimeout(() => {
        document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(heading => {
          heading.removeAttribute('aria-label');
        });
      }, 0);

    return () => {
      if ('cancelIdleCallback' in window) {
        cancelIdleCallback(idleId as number);
      } else {
        clearTimeout(idleId as number);
      }
      if (window) {
        window.removeEventListener("z1_analytic", handleZ1Analytic);
      }
    };
  }, []);

  // WAT-10279: Page-load-modal props — feeds are pre-resolved by GSSP.
  const modalOrchestratorProps: IModalOrchestratorProps = {
    clientSideIsUserAuth,
    exitModalCMS: props?.exitModalCMS,
    globalFeaturesConfig: props?.globalFeaturesConfig,
    idleTimerModal: props?.idleTimerModal,
    marketingAiModalData: props?.marketingAiModalData,
    modalPriorityList: props?.modalPriorityList,
    revisitorModalFeed: props?.revisitorModalFeed
  };

  const renderIndexData = () => {
    try {
      const { err } = props;
      if (err) {
        return <Error statusCode={err.statusCode} />;
      }
      let blok;

      return (
        <>
          <Head
            data={props.feed.pageProperties}
            default={pageTags}
            flag="head"
            globalOverrides={props.globalOverrides}
            page={props.page}
            root={props.root}
          />
          <WnCThemeProvider theme={DtvTheme}>
            <Global
              styles={css`
                    html {
                      scroll-behavior: smooth;
                    }
                    body {
                      margin: 0;
                      background: ${bodyBackground};
                    }
                    p {
                      margin: 0.5em 0em 0.5em;
                    }

                    /* Style Overwrite from Cache */
                    ${props?.styleOverwriteData?.css || ''}
                    html, body {
                      max-width: 100vw;
                    }
                  `}
            />
            <WnCCssBaseline />
            {dtvFlashData && (
              <GlobalModal>
                <>
                  <div id="mainContentAboveGN">
                    <div
                      style={{
                        maxWidth: "2000px",
                        paddingLeft: "0px",
                        paddingRight: "0px",
                        marginLeft: "auto",
                        marginRight: "auto",
                        width: "100%",
                        background: pageBackground
                      }}
                      {...(props.cms_type === "sb" ? dtvFlashData.editableProps : {})}
                    >
                      <DtvFlash {...dtvFlashData} />
                    </div>
                  </div>
                </>
              </GlobalModal>
            )}

            {!props.disableGNav && (
              <span{...props?.globalNavHeader?.editableProps ? { ...props?.globalNavHeader?.editableProps } : ""} >
                <DtvGlobalHeader
                  activeRoute={props.page}
                  customerCareNum={props.customerCareNum}
                  customerCareNumLabel={props.customerCareNumLabel}
                  data={headerData}
                  disableArsFee={props?.globalFeaturesConfig?.enableRR}
                  disableAuxNav={disableAuxNav}
                  disableBusinessLink={props.disableBusinessLink}
                  disableSkipNavigation={disableSkipNavigation}
                  disableStickyGNav={props?.pageThemeObj?.disableStickyGNav || !!dtvFlashData}
                  disableTFN={props.disableTFN}
                  enableCartIconDrawer={props?.globalFeaturesConfig?.enableCartIconDrawer && !isMduPage}
                  enableCartIconDrawerFor={props?.globalFeaturesConfig?.enableCartIconDrawerFor}
                  enableConfigureCart={props?.globalFeaturesConfig?.enableConfigureCart ?? true}
                  env={appEnv as any}
                  helpCenterInfoLabel={props.helpCenterInfoLabel}
                  hideCartIcon={props?.hideCartIcon}
                  hideGN={props.pagePath.includes("modal") || props.page.includes("modal")}
                  isAuth={false}
                  makeRecogme={false}
                  recognised={false}
                  showCart={true}
                  skinnyGnav={props.disableSkinnyGNav}
                  isLightTheme={
                    pageThemeObj?.themeType !== "dark"
                  }
                />
              </span>
            )}

            <GlobalModal>
              <>
                <ClientPageBootstrap />
                <main id="mainContent">
                  <div
                    style={{
                      maxWidth: "2000px",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                      marginLeft: "auto",
                      marginRight: "auto",
                      width: "100%",
                      background: pageBackground
                    }}
                  >
                    <WnCMainCanvas
                      cms_type={props.cms_type}
                      env={appEnv as any}
                      pageInfo={props.feed.pageProperties || {}}
                      pagePath={props?.pagePath}
                      reqQueryParams={props.reqQueryParams}
                      data={
                        blok || sbData || props.data}
                      options={{
                        isAuthenticatedUser: clientSideIsUserAuth,
                        recognizedUserData: clientSideRecognizedUserData,
                        enableConfigureCart: props?.globalFeaturesConfig?.enableConfigureCart ?? true
                      }}
                    />
                    {/* Show Side drawer only if the page as personalization data, and is in lower env's.
                          For local make appEnv === prod, which is being set in setenvbat file */}
                    {(appEnv !== "prod" && props.personalizedData?.length > 0) &&
                      <PersonalizationSideMenu
                        cmsType={props.cms_type}
                        data={props.personalizedData}
                      />
                    }
                    {!(props?.pagePath?.includes?.("modal") || props?.page?.includes?.("modal")) &&
                      <ModalOrchestrator {...modalOrchestratorProps} />
                    }
                  </div>
                  {props?.globalFeaturesConfig?.miniPopupConfig?.[0]?.enableMiniPopup && (
                    <DtvSnackBar miniPopupVal={props?.globalFeaturesConfig?.miniPopupConfig} />
                  )}
                </main>
                {!props.disableLinkFarm && (
                  <DtvLinkFarm {...props?.globalNavLinkFarm} />
                )}
                {(!props.disableGlobalFooter && !(props.pagePath.includes("modal")) && (!props.page.includes("modal"))) && (
                  <DtvFooter
                    data={props.globalNavFooter}
                    enableLangSupport={false}
                    showHelpCenterInfo={true}
                  />
                )}
              </>
            </GlobalModal>
          </WnCThemeProvider>
          <Head
            flag="body"
            globalOverrides={props.globalOverrides}
            page={props.page}
            data={props.feed.pageProperties}
            // default={pageTags}
            root={props.root}
          />
        </>
      );
    } catch (error) {
      return <ErrorDisplay error={error} />;
    }
  };

  return (
    <>
      <DocumentEventHandler />
      {renderIndexData()}
    </>
  );
};

// eslint-disable-next-line complexity
export const getServerSideProps = async ctx => {
  const start = Date.now();
  const personalizedData: any[] = [];

  // Parsing Zip from Akamai Header
  const parseAkamaiZip = (header: string) => (header?.includes("zip=") ? header.split("zip=")[1].substring(0, 5) : null);

  const isAuthenticatedUser = (req: any) => {
    const cAuthNState = req.cookies?.cAuthNState;
    return validateCAuthNStateCookie(cAuthNState || ``);
  };

  const validateCAuthNStateCookie = (cAuthNStateCookie: string): boolean => cAuthNStateCookie && cAuthNStateCookie !== "en";

  if (ctx.req) {
    const additionalParams = ctx?.req?.additionalParams;
    const page = additionalParams?.page || `homepage`;
    const pages = additionalParams?.pages || {};
    const sbConfigs = additionalParams?.sbConfigs;
    const reqQueryParams = ctx.req.query || {};
    const appVariant = additionalParams?.appVariant;
    const svodGenre = additionalParams?.svodGenre;
    const packageConfig = additionalParams?.packageConfig?.packageConfig;
    const onDemandPackageConfig = additionalParams?.packageConfig?.onDemandPackageConfig;
    const genreNationals = additionalParams?.genreNationals;
    const rsnTierLegalTextObj = additionalParams?.packageConfig?.rsnTierLegalText;
    const genrePackOfferIds = additionalParams?.packageConfig?.genrePackOfferIds;
    logPageRequestDebug(page, "getServerSideProps main index", {
      page,
      queryParams: reqQueryParams,
      sbConfigs
    });

    const localPageData = additionalParams?.localPageData || [];
    const currentPage = additionalParams?.currentPage || {};
    const cookieStore = additionalParams?.cookieStore || {};
    const pageProperties = additionalParams?.pageProperties || {};
    const channelListingPageData = additionalParams?.channelListingPageData || {};
    const channelCompareToolPageData = additionalParams?.channelCompareToolPageData || {};
    const epochOffers = additionalParams?.epochOffers || {};
    const applicationFeatureConfig = additionalParams?.applicationFeatureConfig || {};
    // cache still returns this field as 'secrets'; only the client context key is renamed
    const apiKeys = additionalParams?.secrets || {};
    const pageSessionId = additionalParams?.pageSessionId || "";
    const styleOverwriteData = additionalParams?.styleOverwriteData;
    const componentOverwriteData = additionalParams?.componentOverwriteData;

    if (styleOverwriteData?.css) {
      logPageRequestDebug(page, "Style overwrite data found in cache", {
        cssLength: styleOverwriteData.css.length,
        pageSessionId
      });
    } else {
      logPageRequestDebug(page, "No style overwrite data found in cache", { pageSessionId });
    }

    const cmsFeed: string = currentPage?.cmsFeed || ``;
    const cmsApi: string = cmsFeed.replace(`prod/`, ``).replace(`.cmsfeed.js`, ``);
    const cms_type: string = additionalParams?.cms_type || `sb`;

    let previousURL: string | undefined;
    if (cmsApi && cmsFeed) {
      previousURL = ctx.req.headers.referer;
      const personalizationTriggers = {
        cookieStore,
        previousURL,
        queryParam: reqQueryParams,
        userAgent: ctx?.req?.headers?.["user-agent"] || ""
      };

      const pageData = dynamicPageAdapter({
        pageComponents: localPageData?.components,
        personalizedComponents: localPageData?.personalizedComponents,
        personalizationMapping: additionalParams?.pznMapping,
        personalizationTriggers,
        channelListingData: channelListingPageData,
        channelsServiceOtt: additionalParams?.channelsServiceOtt,
        channelsServiceSatellite: additionalParams?.channelsServiceSatellite,
        channelCompareToolPageData,
        queryParamsRequired: localPageData?.queryParamsRequired || [],
        queryParams: reqQueryParams,
        applicationFeatureConfig,
        epochOffers,
        packagesConfig: packageConfig,
        packagesConfigFeed: additionalParams?.packageConfig,
        genrePackOfferIds,
        onDemandPackageConfig,
        svodGenreData: svodGenre,
        embeddedSvods: additionalParams?.embeddedSvods?.subscriptions || [],
        page,
        rsnTierLegalTextObj,
        genreNationals,
        pageSessionId,
        svodDtvsPcp: additionalParams?.svodDtvsPcp,
        svodDtviPcp: additionalParams?.svodDtviPcp
      })

      const components = pageData?.components
      const modalComponents = pageData?.modalComponents || []
      const cookieList = additionalParams?.cookieList?.length > 0 ? [...additionalParams.cookieList, ...(pageData?.cookieList || [])] : pageData?.cookieList || []

      // Debug-level: personalizationTriggers carries the cookie jar + UA.
      logPageRequestDebug(page, "Dynamic adaption completed", {
        page,
        componentsLength: components?.length,
        cookieList,
        personalizationTriggers,
        personalizedComponentsLength: localPageData?.personalizedComponents?.length,
        pageSessionId
      });

      if (reqQueryParams?.componentIndex) {
        try {
          const idxArr = reqQueryParams.componentIndex.split(",").filter(Boolean).map(Number);
          // eslint-disable-next-line no-plusplus
          for (let i = idxArr.length - 1; i >= 0; i--) {
            components.splice(idxArr[i], 1);
          }
        } catch (e) {
          console.error(e);
        }
      }

      const initPagePath = ctx.asPath || ctx.resolvedUrl;

      const priorityModalMap = pageProperties.modalPriorityMap || undefined;
      // WAT-10279: Build sorted priority list for descending fallback
      const modalPriorityList = getSortedModalPriorityList(priorityModalMap);

      // WAT-10279: Resolve modal feeds — page-level overrides global; flags gate global.
      // modalComponents are pre-extracted by dynamicPageAdapter; canvas never sees them.
      const { exitModalCMS, revisitorModalFeed, marketingAiModalData } =
        resolveModalFeeds({
          modalComponents,
          featureConfig: applicationFeatureConfig,
          globalExitFeed: additionalParams?.exitModal,
          globalTeaserCart: additionalParams?.teaserCart,
          packageConfig,
          includeMarketingAi: true
        });

      const isUserLoggedIn = isAuthenticatedUser(ctx.req);
      const locDet = parseAkamaiZip(ctx.req.headers["idpctx-cookie"]);

      // Component Overwrite: Replace components at specified indices
      const overwriteComponents = componentOverwriteData?.components?.[page];
      if (Array.isArray(overwriteComponents) && overwriteComponents.length > 0) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < overwriteComponents.length; i++) {
          components[overwriteComponents[i].index] = overwriteComponents[i].data;
        }
      }

      // HTML Rewriter: Inject raw HTML/CSS/JS at specified indices (Server-Side)
      // const htmlRewriteRules = htmlRewriterData?.rules?.[page];
      // if (Array.isArray(htmlRewriteRules) && htmlRewriteRules.length > 0) {
      //   // Sort rules by index in descending order to maintain correct indices when inserting
      //   const sortedRules = [...htmlRewriteRules].sort((a, b) => b.index - a.index);

      //   for (const rule of sortedRules) {
      //     if (rule.content && typeof rule.index === 'number') {
      //       console.log(`HTML Rewriter: Injecting content at index ${rule.index} using method "${rule.method}"`);

      //       // Parse content to extract styles, scripts, and HTML
      //       let htmlContent = rule.content;
      //       const styles: string[] = [];
      //       const scripts: string[] = [];

      //       // Extract <style> tags
      //       const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
      //       let styleMatch;
      //       while ((styleMatch = styleRegex.exec(rule.content)) !== null) {
      //         styles.push(styleMatch[1]);
      //         htmlContent = htmlContent.replace(styleMatch[0], '');
      //       }

      //       // Extract <script> tags
      //       const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
      //       let scriptMatch;
      //       while ((scriptMatch = scriptRegex.exec(rule.content)) !== null) {
      //         scripts.push(scriptMatch[1]);
      //         htmlContent = htmlContent.replace(scriptMatch[0], '');
      //       }

      //       // Create a custom component with parsed content for SSR
      //       const htmlComponent = {
      //         type: "HTML_REWRITER",
      //         props: {
      //           htmlContent: htmlContent.trim(),
      //           styles: styles,
      //           scripts: scripts,
      //           ruleIndex: rule.index,
      //           options: {
      //             id: `html-rewriter-${rule.index}`
      //           }
      //         }
      //       };

      //       // Apply the method (prepend, append, replace, etc.)
      //       switch (rule.method) {
      //         case 'prepend':
      //           components.splice(rule.index, 0, htmlComponent);
      //           break;
      //         case 'append':
      //           components.splice(rule.index + 1, 0, htmlComponent);
      //           break;
      //         case 'replace':
      //           components[rule.index] = htmlComponent;
      //           break;
      //         case 'before':
      //           components.splice(rule.index, 0, htmlComponent);
      //           break;
      //         case 'after':
      //           components.splice(rule.index + 1, 0, htmlComponent);
      //           break;
      //         default:
      //           components.splice(rule.index, 0, htmlComponent);
      //       }
      //     }
      //   }
      // }

      const initialProps: IndexProps = {
        data: components,
        feedData: {},
        err: !cmsFeed || !cmsApi ? { statusCode: 404 } : undefined,
        feed: {
          pageProperties,
          analytics: pageProperties?.analytics
        },
        page,
        pages: sbConfigs?.sbPreviewAllowed ? pages : null,
        pagePath: initPagePath,
        root: publicRuntimeConfig.root,
        zipCode: locDet || undefined,
        isUserAuthenticated: isUserLoggedIn ?? false,
        // depVersions / onDemandPackageConfig intentionally omitted — they were
        // serialized into __NEXT_DATA__ on every page but have no client-side
        // consumer (verified via repo-wide grep; onDemandPackageConfig is only
        // used server-side inside dynamicPageAdapter above).
        globalNavHeader: additionalParams?.header || "",
        globalNavFooter: additionalParams?.footer || "",
        globalNavLinkFarm: additionalParams?.linkFarm || "",
        idleTimerModal: isUserLoggedIn ? additionalParams?.idleTimerModal || "" : undefined,
        is404Page: additionalParams?.is404Page ?? false,
        reqQueryParams,
        recognizedUserData: ctx.req.userData,
        disableGNav: pageProperties.disableGNav ?? false,
        helpCenterInfoLabel: pageProperties.helpCenterInfoLabel,
        customerCareNum: pageProperties.CustomerCareNum,
        customerCareNumLabel: pageProperties.CustomerCareNumLabel,
        disableTFN: pageProperties.disableTFN ?? false,
        isDoubleBreadcrumbNeeded: pageProperties.isDoubleBreadcrumbNeeded ?? false,
        disableSkinnyGNav: pageProperties.disableSkinnyGNav ?? false,
        disableLinkFarm: pageProperties.disableLinkFarm || (page.includes("modal") && true),
        disableGlobalFooter: pageProperties.disableGlobalFooter,
        hideCartIcon: pageProperties.hideCartIcon ?? false,
        disableAuxNav: pageProperties.disableAuxNav ?? false,
        disableBusinessLink: pageProperties.disableBusinessLink ?? false,
        pageThemeObj: localPageData?.pageTheme || {},
        globalOverrides: pages.globalStyleOverride?.StyleOverride?.elements?.globalOverride,
        isDCP: additionalParams?.isDCP,
        sbConfigs,
        apiPaths: additionalParams?.apiPaths,
        breadcrumbHomeURL: additionalParams?.breadcrumbHomeURL || "https://www.directv.com",
        appVariant,
        cms_type,
        flashProps: localPageData?.flashComponent || null,
        exitModalCMS,
        globalFeaturesConfig: applicationFeatureConfig,
        globalRsnFee: epochOffers?.dtviOffersPrices?.rsnFee || "",
        personalizedData,
        // personalizationMapping: additionalParams?.pznMapping,
        // dmaCookie: cookieStore?.dmaCookie,
        // subscriberType: cookieStore?.subscriberType,
        // utmCodeCookie: cookieStore?.utmCodeCookie,
        // visitedPCP: cookieStore?.visitedPCP,
        // userVisitCount: cookieStore?.userVisitCount,
        cookieList,
        // ovChnlPageData: additionalParams?.ovChnlPageData || {},
        pageBackgrounds: localPageData?.pageBackground || {},
        // pageLevelGnProps: localPageData?.pageLevelGlobalHeader || {},
        modalPriorityList,
        marketingAiModalData,
        // previousURL,
        // embeddedSvods: additionalParams?.embeddedSvods?.subscriptions,
        revisitorModalFeed: revisitorModalFeed || null,
        apiKeys: getClientSafeApiKeys(apiKeys),
        packageConfig,
        // personalizedComponents: localPageData?.personalizedComponents || [],
        // rsnTierLegalTextObj: rsnTierLegalTextObj,
        styleOverwriteData,
        dataSource: additionalParams?.dataSource || "",
        // htmlRewriterData: htmlRewriterData,
        // getIntegrationPartnerOffersPrices: epochOffers?.integrationPartnerOffersPrices,
        pageAdapterProps: additionalParams?.pageAdapterProps || null

      } as IndexProps;

      const props = removeUndefined(initialProps);
      const end = Date.now();
      logPageRequest(page, "Page is ready to render", {
        page,
        getServerSidePropsTime: `${end - start} ms`,
        isUserLoggedIn,
        pageSessionId
      })

      return { props };
    }
  }

  // Do NOT put ctx.req in log meta: the Express request object is circular
  // (req.socket) and JSON.stringify inside the logger would throw.
  logPageError("getServerSideProps", {
    message: `404 page not found`,
    path: ctx?.req?.url || "",
    pageSessionId: ctx?.req?.additionalParams?.pageSessionId
  });
  return { props: { err: { statusCode: 404 } } };
};

export default DTVUFComIndex;
